import { useEffect, useState } from 'react';
import { useLang } from '../i18n';
import { useMediaOverrides } from '../utils/media';
import { PACKAGES } from './packages';
import { DESTINATIONS } from './destinations';
import en from './i18n/en';
import de from './i18n/de';
import fr from './i18n/fr';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Fail-closed rule: only the two core markets are visible before/without the API map.
const CORE_DESTINATION_SLUGS = new Set(['sao-tome-principe', 'cabo-verde']);

// Shared singleton fetch — every hook consumer subscribes instead of firing its own request.
let visCache = null;
let visPromise = null;
const visListeners = new Set();
const loadVisibility = () => {
  if (!visPromise) {
    visPromise = fetch(`${API}/destinations/visibility`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { visCache = d; })
      .catch(() => { visCache = {}; })
      .finally(() => visListeners.forEach((fn) => fn()));
  }
  return visPromise;
};

const useDestinationVisibility = () => {
  const [loaded, setLoaded] = useState(visCache !== null);
  useEffect(() => {
    const notify = () => setLoaded(true);
    visListeners.add(notify);
    loadVisibility();
    return () => visListeners.delete(notify);
  }, []);
  const isVisible = (slug) => {
    if (CORE_DESTINATION_SLUGS.has(slug)) return visCache ? visCache[slug] !== 'inactive' : true;
    return visCache ? visCache[slug] === 'active' : false;
  };
  return { isVisible, loaded };
};

// Official transfers catalog — sourced from the database (Admin-managed), never static.
let trCache = null;
let trPromise = null;
const trListeners = new Set();
const loadTransfers = () => {
  if (!trPromise) {
    trPromise = fetch(`${API}/transfers`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { trCache = Array.isArray(d) ? d : []; })
      .catch(() => { trCache = []; })
      .finally(() => trListeners.forEach((fn) => fn()));
  }
  return trPromise;
};

const useTransfers = () => {
  const [loaded, setLoaded] = useState(trCache !== null);
  useEffect(() => {
    const notify = () => setLoaded(true);
    trListeners.add(notify);
    loadTransfers();
    return () => trListeners.delete(notify);
  }, []);
  return { transfers: trCache || [], loaded };
};

// Official experiences catalog — sourced from the database (Admin-managed, Fase 5).
// Per-language cache: approved translations are applied server-side (?lang=).
let expCache = {};
let expPromise = {};
const expListeners = new Set();
const loadExperiences = (lang) => {
  if (!expPromise[lang]) {
    expPromise[lang] = fetch(`${API}/experiences?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { expCache[lang] = Array.isArray(d) ? d : []; })
      .catch(() => { expCache[lang] = []; })
      .finally(() => expListeners.forEach((fn) => fn()));
  }
  return expPromise[lang];
};

const useExperiencesData = () => {
  const { lang } = useLang();
  const [loaded, setLoaded] = useState(Boolean(expCache[lang]));
  useEffect(() => {
    const notify = () => setLoaded(true);
    expListeners.add(notify);
    if (!expCache[lang]) setLoaded(false);
    loadExperiences(lang);
    return () => expListeners.delete(notify);
  }, [lang]);
  return { dbExperiences: expCache[lang] || [], loaded: loaded && Boolean(expCache[lang]) };
};

// Official packages catalogue — API-first with a controlled static fallback
// while older deployments do not yet expose /api/packages.
// Per-language cache: approved DB translations are applied server-side (?lang=).
let pkgCache = {};
let pkgPromise = {};
const pkgListeners = new Set();
const loadPackages = (lang) => {
  if (!pkgPromise[lang]) {
    pkgPromise[lang] = fetch(`${API}/packages?lang=${lang}`)
      .then(async (r) => (r.ok ? { available: true, data: await r.json() } : { available: false, data: [] }))
      .then((result) => { pkgCache[lang] = result; })
      .catch(() => { pkgCache[lang] = { available: false, data: [] }; })
      .finally(() => pkgListeners.forEach((fn) => fn()));
  }
  return pkgPromise[lang];
};
const usePackagesData = () => {
  const { lang } = useLang();
  const [loaded, setLoaded] = useState(Boolean(pkgCache[lang]));
  useEffect(() => {
    const notify = () => setLoaded(true);
    pkgListeners.add(notify);
    if (!pkgCache[lang]) setLoaded(false);
    loadPackages(lang);
    return () => pkgListeners.delete(notify);
  }, [lang]);
  return { dbPackages: pkgCache[lang]?.available ? pkgCache[lang].data : PACKAGES, loaded: loaded && Boolean(pkgCache[lang]) };
};

// Cruise has no static fallback: an empty API response is the authoritative catalogue.
let cruiseCache = null;
let cruisePromise = null;
const cruiseListeners = new Set();
const loadShoreExcursions = () => {
  if (!cruisePromise) {
    cruisePromise = fetch(`${API}/shore-excursions`)
      .then(async (response) => (response.ok ? await response.json() : []))
      .then((items) => { cruiseCache = Array.isArray(items) ? items : []; })
      .catch(() => { cruiseCache = []; })
      .finally(() => cruiseListeners.forEach((listener) => listener()));
  }
  return cruisePromise;
};
const useShoreExcursionsData = () => {
  const [loaded, setLoaded] = useState(cruiseCache !== null);
  useEffect(() => {
    const notify = () => setLoaded(true);
    cruiseListeners.add(notify); loadShoreExcursions();
    return () => cruiseListeners.delete(notify);
  }, []);
  return { dbShoreExcursions: cruiseCache || [], loaded };
};

const T = { en, de, fr };

// cats/duration stay as stable base values (filters depend on them);
// display translation of durations happens here instead.
const apply = (item, dict) => {
  const tr = dict?.[item.slug];
  if (!tr) return item;
  const { cats, duration, ...rest } = tr;
  return { ...item, ...rest };
};

// A current Admin-approved DB translation is canonical. Legacy build-time copy is
// only a fallback for older Experiences that do not have one yet.
const applyExperience = (item, dict, lang) => (
  lang !== 'pt' && item.content_language === lang ? item : apply(item, dict)
);

// Same rule for Full Packages: an approved DB translation wins over the static overlay.
const applyPackage = (item, dict, lang) => (
  lang !== 'pt' && item.content_language === lang ? item : apply(item, dict)
);

const applyDest = (d, dict, ov) => {
  const tr = dict?.[d.slug];
  const base = {
    ...d,
    img: ov[`dst:${d.slug}:card`] || d.img,
    hero: ov[`dst:${d.slug}:hero`] || d.hero,
    subs: d.subs?.map((s) => ({ ...s, img: ov[`dst:${d.slug}:${s.slug}`] || s.img })),
  };
  const merged = !tr ? base : {
    ...base,
    positioning: tr.positioning ?? base.positioning,
    categories: tr.categories ?? base.categories,
    cta: tr.cta ?? base.cta,
    subs: base.subs?.map((s) => (tr.subs?.[s.slug] ? { ...s, ...tr.subs[s.slug], img: s.img } : s)),
  };
  return merged;
};

export const CAT_KEYS = { Natureza: 'cat.nature', Cultura: 'cat.culture', Mar: 'cat.sea', Gastronomia: 'cat.gastro' };
export const INT_KEYS = { Natureza: 'cat.nature', Praia: 'int.beach', Cultura: 'cat.culture', Gastronomia: 'cat.gastro', Caminhada: 'int.hike', Mar: 'cat.sea', Aventura: 'int.adventure', Relaxamento: 'int.relax' };
export const LANG_KEYS = { Português: 'lang.pt', Inglês: 'lang.en', Francês: 'lang.fr', Alemão: 'lang.de' };

export const useVisibleDestinations = () => {
  const { lang } = useLang();
  const ov = useMediaOverrides();
  const { isVisible } = useDestinationVisibility();
  const dict = T[lang];
  return DESTINATIONS.map((d) => applyDest(d, dict?.destinations, ov)).filter((d) => isVisible(d.slug));
};

export const useCatalog = () => {
  const { lang, t } = useLang();
  const ov = useMediaOverrides();
  const { isVisible, loaded } = useDestinationVisibility();
  const { transfers: dbTransfers, loaded: transfersLoaded } = useTransfers();
  const { dbExperiences, loaded: experiencesLoaded } = useExperiencesData();
  const { dbPackages, loaded: packagesLoaded } = usePackagesData();
  const { dbShoreExcursions, loaded: shoreExcursionsLoaded } = useShoreExcursionsData();
  const dict = T[lang];
  const durLabel = (v) => (v === 'Full Day' ? t('dur.full') : v === 'Half Day' ? t('dur.half') : v === 'Half/Full Day' ? t('dur.halffull') : v);
  const daysLabel = (v) => {
    const m = /^(\d+)\s*dias$/.exec(v || '');
    return m ? t('dur.days', { n: m[1] }) : v;
  };
  const experiences = dbExperiences.map((e) => ({ ...applyExperience(e, dict?.experiences, lang), durationBase: e.duration, duration: durLabel(e.duration), img: ov[`exp:${e.slug}`] || e.img }));
  const packages = dbPackages.map((p) => ({ ...applyPackage(p, dict?.packages, lang), duration: daysLabel(p.duration), img: ov[`pkg:${p.slug}`] || p.img }));
  const shoreExcursions = dbShoreExcursions.map((item) => ({ ...item, img: ov[`cruise:${item.slug}`] || item.img }));
  const transfers = dbTransfers.map((tr) => ({ ...tr, img: ov[`tr:${tr.slug}`] || tr.img }));
  const destinations = DESTINATIONS.map((d) => applyDest(d, dict?.destinations, ov)).filter((d) => isVisible(d.slug));
  return {
    experiences,
    experiencesLoaded,
    packages,
    packagesLoaded,
    shoreExcursions,
    shoreExcursionsLoaded,
    transfers,
    transfersLoaded,
    destinations,
    destinationsLoaded: loaded,
    experienceBySlug: (slug) => experiences.find((e) => e.slug === slug),
    packageBySlug: (slug) => packages.find((p) => p.slug === slug),
    shoreExcursionBySlug: (slug) => shoreExcursions.find((item) => item.slug === slug),
    destinationBySlug: (slug) => destinations.find((d) => d.slug === slug),
  };
};
